import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

import { useIngestionStaffNote } from '../../hooks';
import { IngestionStepLayout } from '../../layout';
import { type CaseSummaryFormData, caseSummarySchema } from '../../schemas';
import { IngestionStepSkeleton } from '../../skeletons';

interface CaseSummaryStepProps {
  matterId: string;
  onSaveComplete?: (success: boolean) => void;
}

/**
 * CaseSummaryStep - Notes step (NOT audit-tracked).
 * Collects intake notes and general notes.
 * This step is not tracked in the audit record.
 */
export const CaseSummaryStep: React.FC<CaseSummaryStepProps> = ({
  matterId,
  onSaveComplete,
}) => {
  const hasHydrated = useRef(false);

  // Fetch existing staff note - for intake notes and general notes
  const { existingData: staffNoteData, isLoading: isLoadingStaffNotes, saveIngestionNotes } =
    useIngestionStaffNote(matterId);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<CaseSummaryFormData>({
    resolver: zodResolver(caseSummarySchema),
  });

  // Pre-populate intake and general notes from staff note
  useEffect(() => {
    if (staffNoteData) {
      hasHydrated.current = true;
      setValue('intake_notes', staffNoteData.intakeNotes || '');
      setValue('general_notes', staffNoteData.generalNotes || '');
    }
  }, [staffNoteData, setValue]);

  const onSubmit = async (data: CaseSummaryFormData) => {
    const hasIntakeNotes = !!data.intake_notes?.trim();
    const hasGeneralNotes = !!data.general_notes?.trim();

    try {
      // Save staff note if there's any content
      if (hasIntakeNotes || hasGeneralNotes) {
        await saveIngestionNotes({
          intakeNotes: data.intake_notes?.trim() || '',
          generalNotes: data.general_notes?.trim() || '',
        });
      }
      toast.success('Notes saved');
      onSaveComplete?.(true);
    } catch {
      toast.error('Failed to save notes');
      onSaveComplete?.(false);
    }
  };

  // Show skeleton during initial load
  if (isLoadingStaffNotes && !hasHydrated.current) {
    return <IngestionStepSkeleton />;
  }

  return (
    <IngestionStepLayout
      title="Notes"
      description="Intake and general notes"
    >
      <form
        id="ingestion-step-form"
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-2"
      >
        <Card>
          <CardContent className="space-y-2 p-2">
            {/* Intake Notes */}
            <div>
              <Label htmlFor="intake_notes">Intake Notes</Label>
              <Textarea
                id="intake_notes"
                {...register('intake_notes')}
                rows={8}
                className="resize-y"
              />
              {errors.intake_notes && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.intake_notes.message}
                </p>
              )}
            </div>

            {/* General Notes */}
            <div>
              <Label htmlFor="general_notes">General Notes</Label>
              <Textarea
                id="general_notes"
                {...register('general_notes')}
                rows={8}
                className="resize-y"
              />
              {errors.general_notes && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.general_notes.message}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </form>
    </IngestionStepLayout>
  );
};
